import { ArrowForwardIcon } from "@chakra-ui/icons";
import { SimpleGrid } from "@chakra-ui/layout";
import {
  Card,
  CardBody,
  CardHeader,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { AgChartLabelFormatterParams } from "ag-charts-community";
import Decimal from "decimal.js";
import {
  useCreditFacilitySummarChartRetrieveSuspense,
  useCreditFacilitySummaryRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { CreditFacilityChartValue } from "gen/model/creditFacilityChartValue";
import { useTranslation } from "react-i18next";
import { MaybernChartWrapper } from "shared/components/chart/MaybernChartWrapper";
import { moneyLabelFormatter } from "shared/components/chart/utils";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import colors from "theme/colors";

export const FundFamilySummaryChartCreditFacility: React.FC<{
  fundFamilyId: string;
  creditFacilityId: string;
}> = ({ fundFamilyId, creditFacilityId }) => {
  const { t } = useTranslation(["fundFamily"]);

  const { data: summaryData } = useCreditFacilitySummaryRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: chartData } = useCreditFacilitySummarChartRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const labelGenerator = (
    key?: string | null,
  ): {
    formatter: (
      params: AgChartLabelFormatterParams<CreditFacilityChartValue>,
    ) => string;
    color?: string;
  } => {
    switch (key) {
      case "amount_outstanding":
        return {
          formatter: (
            params: AgChartLabelFormatterParams<CreditFacilityChartValue>,
          ) =>
            shouldShowLabel(
              params.datum.amount_outstanding,
              decimalOrZero(params.datum.amount_available).add(
                decimalOrZero(params.datum.amount_outstanding),
              ),
            )
              ? `${t("fundFamily:summary.charts.creditFacility.outstanding")}\n${moneyLabelFormatter(
                  {
                    value: params.datum.amount_outstanding,
                  },
                )}`
              : "",
        };
      case "amount_available":
        return {
          formatter: (
            params: AgChartLabelFormatterParams<CreditFacilityChartValue>,
          ) =>
            shouldShowLabel(
              params.datum.amount_available,
              decimalOrZero(params.datum.amount_available).add(
                decimalOrZero(params.datum.amount_outstanding),
              ),
            )
              ? `${t("fundFamily:summary.charts.creditFacility.availableToBorrow")}\n${moneyLabelFormatter(
                  {
                    value: params.datum.amount_available,
                  },
                )}`
              : "",
          color: colors.gray[700],
        };
      default:
        return {
          formatter: () => "",
        };
    }
  };

  if (!chartData.data.length) {
    return null;
  }

  return (
    <Card variant="chart" flex={1}>
      <CardHeader justifyContent="left">
        {t("fundFamily:summary.charts.creditFacility.title")}
      </CardHeader>
      <CardBody px="24px" justifyContent="flex-start">
        <HStack mb="16px" spacing="12px" textStyle="small">
          <SimpleGrid
            columns={2}
            templateColumns="max-content max-content"
            columnGap="18px"
            rowGap="4px"
          >
            <GridItem color="gray.500">
              {t(
                "fundFamily:summary.charts.creditFacility.facilityCommitmentAmount",
              )}
            </GridItem>
            <GridItem textAlign="end">
              {formatMoney(summaryData.commitment_amount)}
            </GridItem>
            <GridItem color="gray.500">
              {t(
                "fundFamily:summary.charts.creditFacility.borrowingBaseAmount",
              )}
            </GridItem>
            <GridItem textAlign="end">
              {formatMoney(summaryData.borrowing_base_amount)}
            </GridItem>
          </SimpleGrid>
          <ArrowForwardIcon w="24px" h="24px" color="gray.700" />
          <Text>
            {t("fundFamily:summary.charts.creditFacility.description")}
          </Text>
        </HStack>
        <MaybernChartWrapper
          {...chartData}
          seriesConfig={{
            direction: "horizontal",
            stacked: true,
            cornerRadius: 8,
            labelGenerator,
          }}
          palette={{
            fills: [colors.cyan[600], colors.gray[100]],
          }}
          xAxis={{
            hideTicks: true,
            label: t(
              "fundFamily:summary.charts.creditFacility.availableToBorrow",
            ),
          }}
          yAxis={{
            hideTicks: true,
            label: t("fundFamily:summary.charts.creditFacility.outstanding"),
          }}
          hideLegend={true}
          height={96}
          padding={{
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}
        />
      </CardBody>
    </Card>
  );
};

const shouldShowLabel = (value: string, total: Decimal) => {
  const valueAsDecimal = decimalOrZero(value);
  const result = valueAsDecimal.div(total);
  return !result.isNaN() && result > new Decimal(0.2);
};
